/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC, useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useTranslation, useTranslationsMap } from "common/utils/i18n"
import { AppState } from "store/store.type"
import { Category } from "types/models/menu"
import { SortDrawer } from "components/drawers/SortDrawer"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { MenuCategoryRow } from "pages/menu/detail/menuOverview/lists/MenuCategoryRow" // TODO: move component from /pages/
import { sortMenuItemNewLogicWithSortedIndexes } from "utils/sort_helper"
import {
  useLazyFetchMenuQuery,
  useSortCategoriesMutation,
} from "queries/menuApi"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { fetchSortCategories } from "utils/rtk_migration_helper"

const rowHeight = 64

interface SortCategoriesDrawerProps {
  visible: boolean
  onClose(): void
}

export const SortCategoriesDrawer: FC<SortCategoriesDrawerProps> = ({
  visible,
  onClose,
}: SortCategoriesDrawerProps) => {
  const dispatch = useDispatch()
  const [sortedIndexes, setSortedIndexes] = useState<[number, number][]>([])
  const [sortCategoriesMutation] = useSortCategoriesMutation()
  const {
    flags: {
      sortCategories: isRtkSortCategoriesEnabled,
      fetchMenu: isRtkFetchMenuEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [fetchMenuRtk] = useLazyFetchMenuQuery()

  const categories = useSelector(
    (state: AppState) => state?.menu?.overview?.categories ?? [],
  )
  const merchantID = useSelector((state: AppState) => state?.menu?.merchantID)
  const sectionID = useSelector(
    (state: AppState) => state?.menu?.overview?.section?.sectionID ?? "",
  )
  const {
    merchantGroupID,
    menuGroupDetail: { menuGroupID = "" },
    isMartDepartmentStructure,
    isSellingTimeStructure,
    enablePartialMenu,
    isUCS,
  } = useSelector((state: AppState) => state.menu)

  useEffect(() => {
    setSortedIndexes([])
  }, [categories])

  const textMap = useTranslationsMap({
    sortCategoriesSuccess: "menu.notifications.sortCategories.successMessage",
  })

  const onSortEnd = async (sortedCategories: Category[]) => {
    TroyWebTracker.sendEvent({
      category: EventCategory.menuOverviewRearrangeCategory,
      action: EventAction.submitRearrangeCategory,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        SECTION_ID: sectionID,
      },
    })

    const sorts = isUCS
      ? sortMenuItemNewLogicWithSortedIndexes({
          originalList: categories,
          sortedIndexes,
          sortKey: "sortOrder",
          itemKey: "categoryID",
        })
      : sortedCategories.map((category, index) => ({
          resourceID: category.categoryID ?? "",
          sortOrder: sortedCategories.length - index,
        }))

    // While migration flags are loading, do not fall back to old Redux: the enabled bit is
    // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
    // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
    // the trigger UI while loading so clicks are not silently dropped.
    if (!isRtkFlagLoading) {
      fetchSortCategories({
        dispatch,
        merchantID,
        isRtkFetchMenuEnabled,
        fetchMenuRtk,
        merchantGroupID,
        sectionID,
        sorts,
        menuGroupID: menuGroupID || undefined,
        successMessage: textMap.sortCategoriesSuccess,
        isMartDepartmentStructure,
        isSellingTimeStructure,
        enablePartialMenu,
        isRtkSortCategoriesEnabled,
        sortCategoriesRtk: sortCategoriesMutation,
      })
    }
  }

  return (
    <SortDrawer
      title={useTranslation("menu.drawers.sort.categories")}
      items={categories}
      visible={visible}
      onClose={() => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeCategory,
          action: EventAction.clickExit,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            SECTION_ID: sectionID,
          },
        })
        onClose()
      }}
      onSave={onSortEnd}
      saveDisabled={isRtkFlagLoading}
      itemSize={rowHeight}
      renderRowContent={(category: Category) => (
        <MenuCategoryRow
          category={category}
          data-testid="sort-categories-drawer"
        />
      )}
      onSort={({ oldIndex, newIndex }) => {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuOverviewRearrangeCategory,
          action: EventAction.rearrangeCategory,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : {
                  MERCHANT_GROUP_ID: merchantGroupID,
                  MENU_GROUP_ID: menuGroupID,
                }),
            SECTION_ID: sectionID,
          },
        })
        if (isUCS) {
          setSortedIndexes(prevState => [...prevState, [oldIndex, newIndex]])
        }
      }}
      onClear={() => {
        setSortedIndexes([])
      }}
      data-testid="sort-categories-drawer"
    />
  )
}
